import { ERROR_CODES } from '@shinetools/errors';

import { OutstandingAgreementType } from '__generated__/GQL';
import assertEventType from 'common/bento/lib/assertEventType';
import getIsAgreementCompleted from 'common/bento/modules/Agreement/libs/getIsAgreementCompleted';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import { getServerErrorCode } from 'common/errors/utils';
import getIsRefused from 'features/Onboarding/lib/getIsRefused';
import onboardingHubMicroExperiment from 'features/Onboarding/lib/onboardingHubMicroExperiment';
import getCompanyComplianceModuleStatus, {
  CompanyComplianceModuleStatus,
} from 'features/Onboarding/modules/CompanyCompliance/libs/getCompanyComplianceModuleStatus';
import isPersonalInfoModuleCompleted from 'features/Onboarding/modules/PersonalInfo/libs/isPersonalInfoModuleCompleted';
import { ApplicantType } from 'features/Onboarding/types';

import { Model } from './model';

export enum Guard {
  HasSelectedPlan = 'hasSelectedPlan',
  HasSignedAgreement = 'hasSignedAgreement',
  IsKycNotReadyError = 'isKycNotReadyError',
  IsRefused = 'isRefused',
  IsMultiKYC = 'isMultiKYC',
  IsAwaitingAccountValidation = 'isAwaitingAccountValidation',
  ShouldAnswerBusinessInfo = 'shouldAnswerBusinessInfo',
  ShouldAnswerPersonalInfo = 'shouldAnswerPersonalInfo',
  ShouldAnswerCompanyCompliance = 'shouldAnswerCompanyCompliance',
}

const guards: MachineGuards<Model, Guard> = {
  hasSelectedPlan: ({ getModuleData }) => {
    const { companyData } = getModuleData();

    if (!companyData) {
      return false;
    }

    return Boolean(companyData.company.metadata.selectedPlanId);
  },

  hasSignedAgreement: ({ getModuleData }) => {
    const { companyData } = getModuleData();

    if (!companyData) {
      return false;
    }

    return getIsAgreementCompleted({
      agreementType: OutstandingAgreementType.Cgu,
      outstandingAgreements: companyData.outstandingAgreements,
    });
  },

  isAwaitingAccountValidation: ({ getModuleData }) =>
    Boolean(
      getModuleData().companyData?.companyUser.isAwaitingAccountValidation,
    ),

  isKycNotReadyError: (context, event) => {
    assertEventType(event, 'error.platform.submitApplication');

    return getServerErrorCode(event.data) === ERROR_CODES.KYC_NOT_READY;
  },

  isMultiKYC: ({ getModuleData }) =>
    Boolean(getModuleData().companyData?.company.isMultiKYC),

  isRefused: ({ getModuleData }) => {
    const { companyData } = getModuleData();

    if (!companyData) {
      return false;
    }

    return getIsRefused(companyData.company.metadata);
  },

  shouldAnswerBusinessInfo: ({ getModuleData }) => {
    const { companyData } = getModuleData();

    if (!companyData) {
      return true;
    }

    const {
      activityType,
      legalForm,
      legalName,
      legalRegistrationNumber,
      registrationDate,
    } = companyData.company.profile;

    return (
      !legalForm ||
      !legalRegistrationNumber ||
      !legalName ||
      !registrationDate ||
      !activityType
    );
  },

  shouldAnswerCompanyCompliance: ({ getModuleData }) => {
    const { companyData, user } = getModuleData();

    if (!companyData) {
      return false;
    }

    const { company, shouldAskSanctionsDeclarationQuestions } = companyData;
    const { isMicro } = company.profile;

    // For non-micro, this step is done in the hub
    if (!isMicro) {
      return false;
    }

    // For micro in the onboarding hub experiment, this step is done in the hub
    if (onboardingHubMicroExperiment.getIsUserInExperiment(user.profile)) {
      return false;
    }

    const companyComplianceModuleStatus = getCompanyComplianceModuleStatus({
      companyProfile: company.profile,
      shouldAskSanctionsDeclarationQuestions,
    });

    return [
      CompanyComplianceModuleStatus.ToDo,
      CompanyComplianceModuleStatus.Refused,
    ].includes(companyComplianceModuleStatus);
  },

  shouldAnswerPersonalInfo: ({ getModuleData }) => {
    const { companyData, user } = getModuleData();

    if (!companyData) {
      return false;
    }

    const { company } = companyData;

    return !isPersonalInfoModuleCompleted({
      applicantType: ApplicantType.AccountHolder,
      companyLegalBeneficiaries: company.legalBeneficiaries,
      companyProfile: company.profile,
      userProfile: user.profile,
    });
  },
};

export default guards;
